import React, { useState, useEffect } from "react";
import "./certificationtable.css";
import { useParams } from "react-router-dom";
import { Table, Button, message, Spin, Modal } from "antd";

import {
  CloseOutlined,
  EyeOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import Instance from "../../../AxiosConfig";

const CertificationTable = () => {
  const { id } = useParams();
  const [certifications, setCertifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [modalImage, setModalImage] = useState(null);

  const fetchCompanyDetails = async () => {
    // const token = localStorage.getItem("token");
    setLoading(true);
    try {
      const response = await Instance.get(`/compony/${id}`,
      //    {
      //   headers: { Authorization: `Bearer ${token}` },
      // }
    );

      if (response.status === 200 || response.status === 201) {
        const certs = response.data.certificationIds || [];
        const mappedCerts = certs.map((cert) => ({
          certificationnumber: cert.certificationNumber,
          standard: cert.standard,
          registrationdate: new Date(
            cert.dateOfRegistration
          ).toLocaleDateString(),
          expirydate: new Date(
            cert.certificationExpiryDate
          ).toLocaleDateString(),
          status: cert.status,
          attachments: cert.attachments || [],
        }));
        setCertifications(mappedCerts);
      }
    } catch (error) {
      console.error("Error fetching company details:", error);
      message.error("Failed to load certifications.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchCompanyDetails();
  }, [id]);

  // ✅ Expand/collapse rows manually
  const handleExpandToggle = (certificationnumber) => {
    setExpandedRowKeys((prevKeys) =>
      prevKeys.includes(certificationnumber)
        ? prevKeys.filter((key) => key !== certificationnumber)
        : [...prevKeys, certificationnumber]
    );
  };

  const columns = [
    {
      title: "Certification Number",
      dataIndex: "certificationnumber",
      key: "certificationnumber",
    },
    {
      title: "Standard",
      dataIndex: "standard",
      key: "standard",
    },
    {
      title: "Registration Date",
      dataIndex: "registrationdate",
      key: "registrationdate",
    },
    {
      title: "Expiry Date",
      dataIndex: "expirydate",
      key: "expirydate",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const lower = status?.toLowerCase() || "";
        const styleMap = {
          active: { bg: "#DFF6E4", color: "#0CBC53" },
          inactive: { bg: "#FFF4E5", color: "#FFA500" },
          default: { bg: "#FDEAEA", color: "#FF4D4F" },
        };
        const style = styleMap[lower] || styleMap.default;
        return (
          <span
            style={{
              backgroundColor: style.bg,
              color: style.color,
              padding: "4px 10px",
              borderRadius: "12px",
              fontWeight: 500,
              display: "inline-block",
              minWidth: "70px",
              textAlign: "center",
            }}
          >
            {status}
          </span>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Button
          type="text"
          icon={<EyeOutlined />}
          onClick={() => handleExpandToggle(record.certificationnumber)}
        />
      ),
    },
  ];

  return (
    <div className="certification-table mt-3">
      {showBanner && (
        <div className="success-banner">
          <span>Document downloaded successfully</span>
          <CloseOutlined
            style={{ cursor: "pointer" }}
            onClick={() => setShowBanner(false)}
          />
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: "center", marginTop: "50px" }}>
          <Spin size="large" />
        </div>
      ) : (
        <Table
          columns={columns}
          dataSource={certifications}
          rowKey="certificationnumber"
          pagination={{
            pageSize: 3,
            total: certifications.length,
            className: "custom-pagination",
          }}
          expandable={{
            expandedRowRender: (record) => (
              <>
                {record.attachments && record.attachments.length > 0 ? (
                  <Table
                    dataSource={record.attachments}
                    rowKey={(item) => item.id || item.fileName}
                    pagination={false}
                    columns={[
                      {
                        title: "File Name",
                        dataIndex: "fileName",
                        key: "fileName",
                      },
                      { title: "Type", dataIndex: "type", key: "type" },
                      {
                        title: "Date",
                        dataIndex: "date",
                        key: "date",
                        render: (d) =>
                          d ? new Date(d).toLocaleDateString() : "—",
                      },
                      {
                        title: "Actions",
                        key: "actions",
                        render: (_, att) => (
                          <div style={{ display: "flex", gap: "10px" }}>
                            <EyeOutlined
                              style={{ fontSize: "16px", cursor: "pointer" }}
                              onClick={() => {
                                if (
                                  att.type?.includes("image") ||
                                  att.fileName?.match(/\.(jpg|jpeg|png|gif)$/i)
                                ) {
                                  setModalImage(att.fileUrl);
                                } else {
                                  window.open(att.fileUrl, "_blank");
                                }
                              }}
                            />
                            <DownloadOutlined
                              style={{ fontSize: "16px", cursor: "pointer" }}
                              onClick={() => {
                                const link = document.createElement("a");
                                link.href = att.url;
                                link.setAttribute(
                                  "download",
                                  att.fileName || "document"
                                );
                                document.body.appendChild(link);
                                link.click();
                                link.remove();
                                setShowBanner(true);
                                setTimeout(() => setShowBanner(false), 3000);
                              }}
                            />
                          </div>
                        ),
                      },
                    ]}
                  />
                ) : (
                  <p style={{ marginLeft: "20px" }}>No attachments found.</p>
                )}
              </>
            ),
            expandedRowKeys,
            onExpand: (expanded, record) =>
              handleExpandToggle(record.certificationnumber),

            // ✅ Disable default expand icon
            expandIcon: () => null,
          }}
          rowClassName={() => "custom-row"}
          className="overflow-y-auto"
        />
      )}

      <Modal
  open={!!modalImage}
  footer={null}
  onCancel={() => setModalImage(null)}
  centered
  width={"90%"} // ✅ Use percentage instead of fixed width
  bodyStyle={{ padding: 0 }}
  className="attachment-modal"
>
  <div className="attachment-modal-body">
    <div className="attachment-preview-container">
      <img
        src={modalImage}
        alt="Attachment Preview"
        className="attachment-preview-img"
        style={{
          width: "100%",
          height: "auto", // ✅ Maintain aspect ratio
          objectFit: "contain",
          display: "block",
        }}
      />
    </div>
  </div>
</Modal>

    </div>
  );
};

export default CertificationTable;
